import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select } from '@/components/ui/Input';
import {
  useLabelMappingActions,
  type CreateLabelMappingBody,
} from '@/hooks/useDispatcher';
import type { LabelMapping, LabelMappingProvider, LabelMappedEvent } from '@/types/api';

const PROVIDERS: { value: LabelMappingProvider; label: string }[] = [
  { value: 'successai', label: 'Success.ai' },
  { value: 'instantly', label: 'Instantly' },
];

const MAPPED_EVENTS: { value: LabelMappedEvent; label: string }[] = [
  { value: 'bounced', label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'not_interested', label: 'Not interested' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  editing?: LabelMapping | null;
}

export function LabelMappingFormModal({ open, onClose, workspaceId, editing }: Props) {
  const { create, update } = useLabelMappingActions(workspaceId);

  const [labelName, setLabelName] = useState('');
  const [labelId, setLabelId] = useState('');
  const [provider, setProvider] = useState<LabelMappingProvider>('successai');
  const [mappedEvent, setMappedEvent] = useState<LabelMappedEvent>('bounced');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (editing) {
      setLabelName(editing.successai_label_name ?? '');
      setLabelId(editing.successai_label_id);
      setProvider(editing.provider);
      setMappedEvent(editing.mapped_event);
      setActive(editing.active);
    } else {
      setLabelName('');
      setLabelId('');
      setProvider('successai');
      setMappedEvent('bounced');
      setActive(true);
    }
  }, [editing, open]);

  const isEditing = !!editing;
  const isPending = create.isPending || update.isPending;

  async function handleSubmit() {
    if (!labelId.trim()) {
      toast.error('Le Label ID est obligatoire');
      return;
    }

    try {
      if (isEditing) {
        await update.mutateAsync({
          id: editing!.id,
          workspace_id: workspaceId,
          provider,
          successai_label_id: labelId.trim(),
          successai_label_name: labelName.trim() || undefined,
          mapped_event: mappedEvent,
          active,
        });
        toast.success('Mapping mis à jour');
      } else {
        const body: CreateLabelMappingBody = {
          workspace_id: workspaceId,
          provider,
          successai_label_id: labelId.trim(),
          successai_label_name: labelName.trim() || undefined,
          mapped_event: mappedEvent,
          active,
        };
        await create.mutateAsync(body);
        toast.success('Mapping ajouté');
      }
      onClose();
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditing ? 'Modifier le mapping' : 'Ajouter un mapping'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} loading={isPending}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Provider</Label>
          <Select value={provider} onChange={(e) => setProvider(e.target.value as LabelMappingProvider)}>
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Label ID</Label>
          <Input
            value={labelId}
            onChange={(e) => setLabelId(e.target.value)}
            placeholder="ex : 6482a1f3c..."
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Pour récupérer l'ID dans Success.ai : ouvre l'inbox, ouvre devtools (F12) onglet Network, applique le label sur un contact, copie le <code className="font-mono">label_id</code> dans le body du POST.
          </p>
        </div>

        <div>
          <Label>Nom du label (optionnel)</Label>
          <Input
            value={labelName}
            onChange={(e) => setLabelName(e.target.value)}
            placeholder="ex : Bounced Hard"
          />
        </div>

        <div>
          <Label>Événement mappé</Label>
          <Select value={mappedEvent} onChange={(e) => setMappedEvent(e.target.value as LabelMappedEvent)}>
            {MAPPED_EVENTS.map((ev) => (
              <option key={ev.value} value={ev.value}>
                {ev.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="label-mapping-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-primary"
          />
          <label htmlFor="label-mapping-active" className="text-sm text-gray-700">
            Actif
          </label>
        </div>
      </div>
    </Dialog>
  );
}
